import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import ToolbarButton from 'components/layout/ToolbarButton';
import LoaderComponent from 'components/LoaderComponent';
import { MandateConsumerRenderProps, withMandateContext } from 'components/MandateContext';
import { PatientConsumerRenderProps } from 'components/patient/PatientContext';
import UpsertDoctorReportModal from 'components/patient/upsert/UpsertDoctorReportModal';
import UpsertPhysiotherapyReportModal from 'components/patient/upsert/UpsertPhysiotherapyReportModal';
import UpsertSynopsisReportModal from 'components/patient/upsert/UpsertSynopsisReportModal';
import moment from 'moment';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header, Icon, Message, Popup } from 'semantic-ui-react';
import { getAllCountries } from 'services/countryServices';
import { getAllLanguages } from 'services/languageServices';
import { fastPrintPatientDocuments } from 'services/patientServices';
import styled from 'styled-components';
import { CountryDto, LanguageDto, PatientDto, PdfResponse, UpsertPatientDto } from 'ts-types/api.types';
import { ChromeExtensionStatus, getChromeExtensionStatus, getPrinters, printDocument } from 'utils/chomeExtensionUtils';
import { noop } from 'utils/functionUtils';
import { getGenderTextKey } from 'utils/localizationUtils';

const PatientInfoContainer = styled.div`
  flex: 1 100%;
`;

const PatientDataLoaderContainer = styled.div`
  height: 100px;
  position: relative;
`;

const PatientDataDiv = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  padding-bottom: 0.275rem;
`;

const PatientMandateDataDiv = styled.div`
  position: absolute;
  bottom: 0.5rem;
  color: #757575;
  font-size: 0.95rem;
`;

const PatientContactInfoDiv = styled.div`
  flex-grow: 1;
  margin-right: -0.875rem;
  padding-bottom: 1.125rem;
  text-align: right;
`;

const PatientDataLeftDiv = styled.div`
  flex-grow: 2;
`;

const PatientDataRightDiv = styled.div`
  margin-left: 0.75rem;
  text-align: end;
`;

const NoDataSpan = styled.span`
  color: #757575;
  font-style: italic;
`;

const PatientName = styled.div`
  font-weight: bold;
  font-size: 1.2rem;
  color: #444;
`;

const PatientId = styled.div`
  margin-top: 0.375rem;
  font-weight: bold;
  font-size: 1rem;
  color: #444;
`;

const PatientExternalIdDiv = styled.div`
  margin-top: 0.375rem;
  font-weight: bold;
  font-size: 1rem;
  color: #444;

  .patient-sex {
    cursor: pointer;
  }

  .external-id-container {
    display: inline-flex;
  }

  .external-id {
    padding-left: 1rem;
  }

  .icon:only-child {
    margin-right: 0;
    vertical-align: 2px;
  }
`;

const SectionDiv = styled.div`
  margin-top: 0.375rem;
`;

const InfoDiv = styled.div`
  & .info-message {
    margin-top: 2rem;
    min-height: 105px;
  }

  & .info-message .download-link {
    margin-top: 1.5rem;
  }

  & .actions .button:first-child {
    margin-left: 0;
  }
`;

const PopupErrorMessage = styled(Message)`
  min-width: 27rem;
`;

const btnStyle = {
  background: 'transparent',
  cursor: 'pointer',
  padding: '5px 5px'
};


interface Props extends AuthConsumerRenderProps,
    MandateConsumerRenderProps,
    MessageSourceProps,
    PatientConsumerRenderProps {
}

export interface VaccinationError {
  errorKey: string,
  fields: string[]
}

interface State {
  printLabelPopupOpen: boolean,
  printLabelPopupStatus: string,
  vaccinationCard?: PdfResponse,
  vaccinationCardPdfModalOpen: boolean,
  vaccinationError?: VaccinationError,
  vaccinationPatientEditOpen: boolean,
  synopsisModalOpen: boolean,
  physiotherapyModalOpen: boolean,
  doctorReportModalOpen: boolean,
  editPatientId?: number,
  editPatient?: UpsertPatientDto,
  countries: Array<CountryDto>,
  languages: Array<LanguageDto>,
  cancelTokenSource: CancelTokenSource
}

class PatientInfoComponent extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const {currentPatient: patient, getMessage}: Props = props;

    const cancelTokenSource = axios.CancelToken.source();

    getAllCountries(cancelTokenSource).then(response => {
      if (!this.mounted) {
        return
      }

      this.setState({
        countries: response
      });
    }).catch(noop);

    getAllLanguages(cancelTokenSource).then(response => {
      if (!this.mounted) {
        return
      }

      this.setState({
        languages: response
      });
    }).catch(noop);

    this.state = {
      printLabelPopupOpen: false,
      printLabelPopupStatus: "none",
      vaccinationCard: undefined,
      vaccinationCardPdfModalOpen: false,
      vaccinationPatientEditOpen: false,
      synopsisModalOpen: false,
      physiotherapyModalOpen: false,
      doctorReportModalOpen: false,
      countries: [],
      languages: [],
      cancelTokenSource
    };
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  popupTrigger = () => {

    const {currentPatient: patient, getMessage}: Props = this.props;

    if (!patient) {
      return;
    }

    return (
        <ToolbarButton iconName="phone"
                       iconSize="large"
                       disabled={this.hasNoContactInfo(patient)}
                       dataTooltip={getMessage("patient.toolbar.patientContactInfo")}
        />
    );
  };

  hasNoContactInfo = (patient: PatientDto): boolean => {
    const {mobilePhone, telephone, telOffice, fax} = patient;
    return !(mobilePhone || telephone || telOffice || fax);
  };

  popupContent = () => {

    const {currentPatient: patient, getMessage} = this.props;

    if (patient) {

      const {mobilePhone, telephone, telOffice, fax} = patient;

      if (this.hasNoContactInfo(patient)) {
        return (
            <NoDataSpan>{getMessage("noContactInfo")}</NoDataSpan>
        )
      }

      return (
          <Grid>
            <Grid.Row><Grid.Column><Header>{getMessage("contactInfo")}</Header></Grid.Column></Grid.Row>
            {mobilePhone &&
                <Grid.Row><Grid.Column>{getMessage("patient.mobilePhone") + ": " + mobilePhone}</Grid.Column></Grid.Row>}
            {telephone &&
                <Grid.Row><Grid.Column>{getMessage("patient.telephone") + ": " + telephone}</Grid.Column></Grid.Row>}
            {telOffice &&
                <Grid.Row><Grid.Column>{getMessage("patient.telOffice") + ": " + telOffice}</Grid.Column></Grid.Row>}
            {fax && <Grid.Row><Grid.Column>{getMessage("patient.fax") + ": " + fax}</Grid.Column></Grid.Row>}
          </Grid>
      )
    }
  };

  triggerFastPrintPatientDocuments = async () => {
    const {currentPatient: patient, config} = this.props;

    this.setState({
      printLabelPopupOpen: true,
      printLabelPopupStatus: "STARTED"
    })

    const extStatus = await getChromeExtensionStatus(config);

    if (ChromeExtensionStatus.NO_EXTENSION === extStatus) {
      this.setState({
        printLabelPopupStatus: extStatus
      });
    } else if (ChromeExtensionStatus.OUTDATED_EXTENSION === extStatus) {
      this.setState({
        printLabelPopupStatus: extStatus
      });
    } else if (ChromeExtensionStatus.NO_HOST_APPLICATION === extStatus) {
      this.setState({
        printLabelPopupStatus: extStatus
      });
    } else if (ChromeExtensionStatus.OUTDATED_HOST_APP === extStatus) {
      this.setState({
        printLabelPopupStatus: extStatus
      });
    } else if (ChromeExtensionStatus.OK === extStatus) {
      this.setState({
        printLabelPopupStatus: "RETRIEVING_PRINTERS"
      });

      const printersResult: any = await getPrinters();
      const printers = printersResult.payload;
      // @ts-ignore
      const labelPrinter: any = printers.find(printer => {
        const printerName: string = printer.name.toLowerCase();
        return printerName.includes("dymo") && printerName.includes("450");
        // return printerName.includes("microsoft") && printerName.includes("pdf");
      });

      if (labelPrinter) {
        this.setState({
          printLabelPopupStatus: "CREATING_LABELS"
        });
        const documents = await fastPrintPatientDocuments(this.state.cancelTokenSource, patient!.id);

        this.setState({
          printLabelPopupStatus: "PRINTING_LABELS"
        });

        for (const doc of documents) {
          await printDocument(doc, labelPrinter.name);
        }

        this.setState({
          printLabelPopupStatus: "DONE"
        });
      } else {
        this.setState({
          printLabelPopupStatus: "PRINTER_NOT_FOUND"
        });
      }
    }
  };

  closePrintLabelPopup = () => {
    this.setState({
      printLabelPopupOpen: false
    });
  }

  printLabelPopupContent = () => {
    const {getMessage} = this.props;
    const {printLabelPopupStatus} = this.state;

    let content = () => {
      switch (printLabelPopupStatus.toUpperCase()) {
        case 'STARTED' :
          return <div>...</div>;

        case 'NO_EXTENSION' :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.NO_EXTENSION')}
              <div className="download-link">
                <a href="https://chrome.google.com/webstore/detail/iggeppehjnmjeeicldkpjopdofglljei?authuser=0&hl=en"
                   target="_blank">{getMessage('nativeTools.download.chromeExtension')}</a>
              </div>
            </div>
          </InfoDiv>;

        case 'OUTDATED_EXTENSION' :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.OUTDATED_EXTENSION')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.chromeExtension')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case 'NO_HOST_APPLICATION' :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.NO_HOST_APPLICATION')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.hostApplication')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case 'OUTDATED_HOST_APP' :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.OUTDATED_HOST_APP')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.hostApplication')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case 'PRINTER_NOT_FOUND' :
          return <div>The label printer was not found.</div>;

        case 'RETRIEVING_PRINTERS' :
          return <div>Retrieving available printers...</div>;

        case 'CREATING_LABELS' :
          return <div>Creating labels...</div>;

        case 'PRINTING_LABELS' :
          return <div>Printing labels...</div>;

        case 'DONE' :
          return <div>Labels printed.</div>;
      }
    }

    return (
        <div>
          {content()}
          <div>
            <Button type="button" compact onClick={this.closePrintLabelPopup}>Close</Button>
          </div>
        </div>
    )
  }

  /*vaccinationCardErrorPopupContent = () => {
    const {getMessage} = this.props;
    const {vaccinationError} = this.state;

    if (!vaccinationError) {
      return <></>;
    }

    return <PopupErrorMessage
        negative
        icon="warning sign"
        header={getMessage(vaccinationError!.errorKey)}
        list={vaccinationError!.fields.map(field => getMessage(`patient.vaccinationCard.error.field.${field}`))}
    />;
  }*/

  /*openVaccinationCardDocument = async (patientId: number) => {
    this.setState({
      vaccinationError: undefined
    });

    try {
      const vaccinationCard: PdfResponse =
          await vaccinationCardDocument(patientId, false, this.state.cancelTokenSource);
      this.openVaccinationCardPdfModal(vaccinationCard);
    } catch (ex) {
      const {response} = ex;

      let errorMessage: VaccinationError | undefined;
      if (response && response.data && response.data.errorCode === 'INVALID_INPUT') {
        const {violations} = response.data;

        let fields: string[] = [];
        if (violations && violations.length > 0) {
          fields = violations.map((violation: any) => violation.field);
        }
        errorMessage = {
          errorKey: 'patient.vaccinationCard.error.invalidPatient',
          fields
        };

        this.setState({
          vaccinationError: errorMessage
        });

        this.openVaccinationPatientEditModal();
      } else {
        this.setState({
          vaccinationError: errorMessage
        });
      }
    }
  };*/

  /*openVaccinationCardMandateDocument = async (patientId: number) => {
    this.setState({
      vaccinationError: undefined
    });

    try {
      const vaccinationCard: PdfResponse = await vaccinationCardMandateDocument(patientId, this.state.cancelTokenSource);
      this.openVaccinationCardPdfModal(vaccinationCard);
    } catch (ex) {
      const {response} = ex;

      let errorMessage: VaccinationError | undefined;
      if (response && response.data && response.data.errorCode === 'INVALID_INPUT') {
        const {violations} = response.data;

        let fields: string[] = [];
        if (violations && violations.length > 0) {
          fields = violations.map((violation: any) => violation.field);
        }
        errorMessage = {
          errorKey: 'patient.vaccinationCard.error.invalidPatient',
          fields
        };

        this.setState({
          vaccinationError: errorMessage
        });

        this.openVaccinationPatientEditModal();
      } else {
        this.setState({
          vaccinationError: errorMessage
        });
      }
    }
  };*/

  openVaccinationCardPdfModal = (pdfResponse: PdfResponse) => {
    this.setState({
      vaccinationCardPdfModalOpen: true,
      vaccinationCard: pdfResponse
    })
  };

  /*closeVaccinationCardPdfModal = () => {

    this.setState({
      vaccinationCardPdfModalOpen: false,
      vaccinationCard: undefined
    });
  };*/

  /*openVaccinationPatientEditModal = async () => {
    const {currentPatient: patient} = this.props;
    const {cancelTokenSource} = this.state;

    findPatientById(cancelTokenSource, patient!.id).then(response => {
      this.setState({
        editPatientId: patient!.id,
        editPatient: response
      });
      this.toggleVaccinationPatientModal();
    }).catch(noop);
  };*/

  /*toggleVaccinationPatientModal = () => {
    this.setState(({vaccinationPatientEditOpen}: State) => ({
      vaccinationPatientEditOpen: !vaccinationPatientEditOpen
    }));
  };*/

  openSynopsisModal = () => {
    this.setState({
      synopsisModalOpen: true
    })
  }

  closeSynopsisModal = () => {
    this.setState({
      synopsisModalOpen: false
    })
  }

  openPhysiotherapyModal = () => {
    this.setState({
      physiotherapyModalOpen: true
    })
  }

  closePhysiotherapyModal = () => {
    this.setState({
      physiotherapyModalOpen: false
    })
  }

  openDoctorReportModal = () => {
    this.setState({
      doctorReportModalOpen: true
    })
  }

  closeDoctorReportModal = () => {
    this.setState({
      doctorReportModalOpen: false
    })
  }

  /*onPatientSave = (patient: PatientDto) => {
    // TODO vg 2021-01-26: the patient on the home page is not updated, since that triggers
    // most of the components to rerender
    // this.props.changePatient(patient);

    this.toggleVaccinationPatientModal();

    this.openVaccinationCardDocument(patient.id);
  };*/

  render = () => {
    const {
      currentPatient: patient,
      patientLoading,
      // currentMandates,
      // getAllMandates,
      getMessage
    } = this.props;

    const {
      printLabelPopupOpen,
      // vaccinationCardPdfModalOpen,
      // vaccinationCard,
      // vaccinationError,
      // vaccinationPatientEditOpen,
      synopsisModalOpen,
      physiotherapyModalOpen,
      doctorReportModalOpen
      // editPatientId,
      // editPatient,
      // countries,
      // languages
    } = this.state;

    const inactiveTag = patient && patient.active ? "" : "[" + getMessage("inactive") + "]";


    let patientSexSpan = undefined;
    let externalIdContent = undefined;
    if (patient) {

      patientSexSpan = <span
          className="patient-sex"
          title={getMessage(getGenderTextKey(patient.sex))}
      >
        , *{getMessage(getGenderTextKey(patient.sex, true))}
      </span>;

      if (patient.externalId && patient.externalIdManual) {
        externalIdContent = (
            <PatientExternalIdDiv>
              <span className={"external-id-container"}>
                <div>[{patient.externalIdManual}]</div>
                <div className={"external-id"}>[<Icon name={"mobile alternate"}
                                                      size={"small"}/>{patient.externalId}]</div>
              </span>
            </PatientExternalIdDiv>)
      } else if (patient.externalIdManual) {
        externalIdContent = (
            <PatientExternalIdDiv>
              [{patient.externalIdManual}]
            </PatientExternalIdDiv>)
      } else if (patient.externalId) {
        externalIdContent = (
            <PatientExternalIdDiv>
              [<Icon name={"mobile alternate"} size={"small"}/>{patient.externalId}]
            </PatientExternalIdDiv>)
      }
    }

    const content = !patientLoading ? (
        <React.Fragment>
          {patient ?
              <React.Fragment>
                <PatientDataDiv>
                  <PatientDataLeftDiv>
                    <SectionDiv>
                      {patient.title ? <PatientName>{patient.title}</PatientName> : null}
                      <PatientName>{patient.name} {patient.firstName} {patientSexSpan} {inactiveTag}</PatientName>
                    </SectionDiv>
                    <SectionDiv>
                      <div>{patient.street}</div>
                      <div>{patient.zip} {patient.city}</div>
                    </SectionDiv>
                  </PatientDataLeftDiv>

                  <PatientDataRightDiv>
                    <PatientId>[{patient.id}]</PatientId>
                    {externalIdContent !== undefined && externalIdContent}
                    <SectionDiv>
                      <div>{moment(patient.birthDate).format("DD.MM.YYYY")}</div>
                    </SectionDiv>
                  </PatientDataRightDiv>
                </PatientDataDiv>

                <PatientDataDiv>
                  <PatientContactInfoDiv>
                    <Popup
                        trigger={this.popupTrigger()}
                        content={this.popupContent()}
                        on="click"
                        size="small"
                        position="bottom center"
                    />
                    <Popup
                        trigger={
                          <ToolbarButton
                              iconName="print"
                              iconSize="large"
                              onClick={this.triggerFastPrintPatientDocuments}
                              dataTooltip={getMessage("patient.toolbar.praxMedicNativeTools")}
                          />
                        }
                        open={printLabelPopupOpen}
                        content={this.printLabelPopupContent()}
                        on="click"
                        onOpen={() => {
                        }}
                        onClose={() => {
                        }}
                        size="small"
                        position="bottom center"
                    />
                    {/*<Popup
                        trigger={
                          <ToolbarButton
                              iconName="syringe"
                              iconSize="large"
                              onClick={() => this.openVaccinationCardDocument(patient!.id)}
                              customTrigger={(
                                  <Icon.Group size="large">
                                    <Icon name="syringe" color="red"/>
                                  </Icon.Group>
                              )}
                              dataTooltip={getMessage("patient.toolbar.vaccinationCard")}
                          />
                        }
                        open={false}
                        content={this.vaccinationCardErrorPopupContent()}
                        on="click"
                        onOpen={() => {
                        }}
                        onClose={() => {
                          this.setState({
                            vaccinationError: undefined
                          })
                        }}
                        size="small"
                        position="bottom center"
                    />*/}
                    {/*{
                        vaccinationCardPdfModalOpen &&
                        <PdfModal
                            modalOpen={vaccinationCardPdfModalOpen}
                            onCloseModal={this.closeVaccinationCardPdfModal}
                            pdfResponses={[vaccinationCard]}
                            getPdfUrl={getPdfUrl}
                        />
                    }*/}
                    {/*<Popup
                        trigger={
                          <ToolbarButton
                              iconName="syringe"
                              iconSize="large"
                              onClick={() => this.openVaccinationCardMandateDocument(patient!.id)}
                              customTrigger={(
                                  <Icon.Group size="large">
                                    <Icon name="syringe" color="grey"/>
                                  </Icon.Group>
                              )}
                              dataTooltip={getMessage("patient.toolbar.vaccinationCardMandate")}
                          />
                        }
                        open={false}
                        content={this.vaccinationCardErrorPopupContent()}
                        on="click"
                        onOpen={() => {
                        }}
                        onClose={() => {
                          this.setState({
                            vaccinationError: undefined
                          })
                        }}
                        size="small"
                        position="bottom center"
                    />*/}
                    {/*{
                        vaccinationCardPdfModalOpen &&
                        <PdfModal
                            modalOpen={vaccinationCardPdfModalOpen}
                            onCloseModal={this.closeVaccinationCardPdfModal}
                            pdfResponses={[vaccinationCard]}
                            getPdfUrl={getPdfUrl}
                        />
                    }*/}
                    <Popup
                        trigger={
                          <ToolbarButton
                              iconName="clipboard outline"
                              iconSize="large"
                              onClick={() => this.openSynopsisModal()}
                              customTrigger={(
                                  <Icon.Group size="large">
                                    <Icon name="clipboard outline"/>
                                  </Icon.Group>
                              )}
                              dataTooltip={getMessage("patient.toolbar.synopsisModal")}
                          />
                        }
                        open={false}
                    />
                    {
                        synopsisModalOpen &&
                        <UpsertSynopsisReportModal
                            modalOpen={synopsisModalOpen}
                            onCloseModal={this.closeSynopsisModal}
                            patient={patient}
                        />
                    }
                    <Popup
                        trigger={
                          <ToolbarButton
                              iconName="heartbeat"
                              iconSize="large"
                              onClick={() => this.openPhysiotherapyModal()}
                              customTrigger={(
                                  <Icon.Group size="large">
                                    <Icon name="heartbeat"/>
                                  </Icon.Group>
                              )}
                              dataTooltip={getMessage("patient.toolbar.physicaltherapyModal")}
                          />
                        }
                        open={false}
                    />
                    {
                        physiotherapyModalOpen &&
                        <UpsertPhysiotherapyReportModal
                            modalOpen={physiotherapyModalOpen}
                            onCloseModal={this.closePhysiotherapyModal}
                            patient={patient}
                        />
                    }
                    <Popup
                        trigger={
                          <ToolbarButton
                              iconName="user doctor"
                              iconSize="large"
                              onClick={() => this.openDoctorReportModal()}
                              customTrigger={(
                                  <Icon.Group size="large">
                                    <Icon name="user doctor"/>
                                  </Icon.Group>
                              )}
                              dataTooltip={getMessage("patient.toolbar.doctorReportModal")}
                          />
                        }
                        open={false}
                    />
                    {
                        doctorReportModalOpen &&
                        <UpsertDoctorReportModal
                            modalOpen={doctorReportModalOpen}
                            onCloseModal={this.closeDoctorReportModal}
                            patient={patient}
                        />
                    }
                  </PatientContactInfoDiv>
                </PatientDataDiv>

                {/*{vaccinationPatientEditOpen &&
                    <UpsertPatient
                        modalOpen={vaccinationPatientEditOpen}
                        toggleModal={this.toggleVaccinationPatientModal}
                        countries={countries}
                        languages={languages}
                        patient={editPatient}
                        patientId={editPatientId}
                        onPatientSave={this.onPatientSave}
                        currentMandates={currentMandates}
                        getAllMandates={getAllMandates}
                        error={vaccinationError}
                        editMode={PatientEditMode.VACCINATION}
                    />
                }*/}

                <PatientMandateDataDiv>
                  {patient.mandateName + ", " + patient.mandateCity}
                </PatientMandateDataDiv>
              </React.Fragment>

              : <div>
                <NoDataSpan>{getMessage('patient.info.empty')}</NoDataSpan>
              </div>
          }

        </React.Fragment>
    ) : (
        <PatientDataLoaderContainer>
          <LoaderComponent
              message={`${getMessage("loading")} ${getMessage("patient").toLowerCase()}`}
              dimmerProps={{active: true, inverted: true}}
          />
        </PatientDataLoaderContainer>
    );

    return (
        <PatientInfoContainer>
          {content}
        </PatientInfoContainer>
    );
  }
}

const PatientInfo = withAuthContext(withMandateContext(withMessages(PatientInfoComponent)));
export default PatientInfo;
